import { useState }          from 'react';
import { useNavigate }       from 'react-router-dom';
import { useCartStore }      from '../../store/cartStore.js';
import { useTerminalStore }  from '../../store/terminalStore.js';
import { useTabsStore }       from '../../store/tabsStore.js';
import CustomerPickerModal    from '../CustomerPickerModal.jsx';
import ItemEditSheet          from '../ItemEditSheet.jsx';
import CashMovementModal      from '../CashMovementModal.jsx';
import { IcoClose, IcoCash, IcoReceipt, IcoTrash, IcoTabs, IcoUser, IcoEdit, IcoArrowUp, IcoWarning } from '../common/Icons.jsx';
import styles from './CartPanel.module.css';

const fmt = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

export default function CartPanel() {
  const navigate      = useNavigate();
  const items         = useCartStore((s) => s.items);
  const customer      = useCartStore((s) => s.customer);
  const discount      = useCartStore((s) => s.discount);
  const subtotal      = useCartStore((s) => s.getSubtotal());
  const total         = useCartStore((s) => s.getTotal());
  const updateQty     = useCartStore((s) => s.updateQuantity);
  const removeItem    = useCartStore((s) => s.removeItem);
  const clearCart     = useCartStore((s) => s.clearCart);
  const setDiscount   = useCartStore((s) => s.setDiscount);
  const note          = useCartStore((s) => s.note);
  const setNote       = useCartStore((s) => s.setNote);

  const terminalStatus = useTerminalStore((s) => s.status);
  const terminalReader = useTerminalStore((s) => s.reader);

  const tabs          = useTabsStore((s) => s.tabs);
  const activeTabId   = useTabsStore((s) => s.activeTabId);
  const createTab     = useTabsStore((s) => s.createTab);
  const updateTab     = useTabsStore((s) => s.updateTab);
  const clearActiveTab = useTabsStore((s) => s.clearActiveTab);
  const activeTab     = tabs.find((t) => t.id === activeTabId) ?? null;

  const [pickerOpen,   setPickerOpen]   = useState(false);
  const [discOpen,     setDiscOpen]     = useState(false);
  const [discType,     setDiscType]     = useState('pct');
  const [discValue,    setDiscValue]    = useState('');
  const [editItem,     setEditItem]     = useState(null);
  const [noteOpen,     setNoteOpen]     = useState(false);
  const [saveTabOpen,    setSaveTabOpen]    = useState(false);
  const [tabName,        setTabName]        = useState('');
  const [movementOpen,   setMovementOpen]   = useState(false);

  const handleSaveTab = () => {
    if (!tabName.trim() && !activeTabId) return;
    if (activeTabId) {
      updateTab(activeTabId, { items, customer, discount, note });
    } else {
      createTab(tabName, { items, customer, discount, note });
      clearCart();
    }
    setSaveTabOpen(false);
    setTabName('');
  };

  const applyDiscount = () => {
    const v = Number(discValue);
    if (!v || v <= 0) { setDiscOpen(false); return; }
    const amount = discType === 'pct'
      ? Math.min((subtotal * v) / 100, subtotal)
      : Math.min(v, subtotal);
    setDiscount(amount);
    setDiscOpen(false);
    setDiscValue('');
  };

  const clearDiscount = () => {
    setDiscount(0);
    setDiscOpen(false);
    setDiscValue('');
  };

  return (
    <aside className={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {activeTab ? (
            <div className={styles.activeTabBadge}>
              <span className={styles.activeTabDot} />
              <span className={styles.activeTabName}>{activeTab.name}</span>
              <button
                className={styles.activeTabClose}
                title="Desvincular cuenta (no la elimina)"
                onClick={() => { clearActiveTab(); clearCart(); }}
              ><IcoClose size={12} /></button>
            </div>
          ) : (
            <span className={styles.title}>Carrito</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          <button
            className="btn btn--ghost"
            style={{ fontSize: 12, padding: '0 8px', minHeight: 32 }}
            onClick={() => setMovementOpen(true)}
            title="Movimiento de caja"
          >
            <IcoCash size={16} />
          </button>
          <button
            className="btn btn--ghost"
            style={{ fontSize: 12, padding: '0 8px', minHeight: 32 }}
            onClick={() => navigate('/sales-history')}
            title="Historial de ventas"
          >
            <IcoReceipt size={16} />
          </button>
          {items.length > 0 && (
            <button
              className="btn btn--ghost"
              style={{ fontSize: 12, padding: '0 8px', minHeight: 32 }}
              onClick={() => { if (window.confirm('¿Vaciar el carrito?')) { clearCart(); clearActiveTab(); } }}
              title="Limpiar carrito"
            >
              <IcoTrash size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Tab save / update bar */}
      {items.length > 0 && (
        <div className={styles.tabBar}>
          {saveTabOpen && !activeTabId ? (
            <div className={styles.tabSaveRow}>
              <input
                className={styles.tabNameInput}
                placeholder="Nombre de la cuenta (Mesa 3, Juan…)"
                value={tabName}
                onChange={(e) => setTabName(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTab(); if (e.key === 'Escape') setSaveTabOpen(false); }}
              />
              <button className="btn btn--primary" style={{ fontSize: 12, padding: '0 10px', minHeight: 32, flexShrink: 0 }} onClick={handleSaveTab}>
                Guardar
              </button>
              <button className="btn btn--ghost"  style={{ fontSize: 12, padding: '0 8px',  minHeight: 32, flexShrink: 0 }} onClick={() => setSaveTabOpen(false)}>
                <IcoClose size={13} />
              </button>
            </div>
          ) : activeTabId ? (
            <button
              className={styles.updateTabBtn}
              onClick={handleSaveTab}
            >
              <IcoArrowUp size={13} /> Guardar cambios en "{activeTab?.name}"
            </button>
          ) : (
            <button
              className={styles.saveTabBtn}
              onClick={() => { setSaveTabOpen(true); setTabName(''); }}
            >
              <IcoTabs size={14} /> Guardar como cuenta…
            </button>
          )}
        </div>
      )}

      {/* Cliente */}
      <button className={styles.customerRow} onClick={() => setPickerOpen(true)}>
        {customer ? (
          <span className={styles.customerName}><IcoUser size={13} /> {customer.name}</span>
        ) : (
          <span className={styles.customerEmpty}>+ Agregar cliente</span>
        )}
        <span className={styles.customerEdit}><IcoEdit size={13} /></span>
      </button>

      {pickerOpen && <CustomerPickerModal onClose={() => setPickerOpen(false)} />}
      {editItem && <ItemEditSheet item={editItem} onClose={() => setEditItem(null)} />}
      {movementOpen && <CashMovementModal onClose={() => setMovementOpen(false)} />}

      {/* Terminal */}
      <button className={styles.terminalRow} onClick={() => navigate('/reader-picker')}>
        <span className={`${styles.terminalDot} ${terminalStatus === 'connected' ? styles.terminalDotOn : ''}`} />
        <span className={terminalStatus === 'connected' ? styles.terminalName : styles.terminalEmpty}>
          {terminalStatus === 'connected'
            ? (terminalReader?.label ?? terminalReader?.serialNumber ?? 'Lector conectado')
            : 'Sin lector'}
        </span>
        <span className={styles.customerEdit}>›</span>
      </button>

      {/* Items */}
      <div className={styles.items}>
        {items.length === 0 ? (
          <div className={styles.empty}>Agrega productos del catálogo</div>
        ) : (
          items.map((item) => {
            const discounted  = item.unit_price < item.product.price;
            const overStock   = item.product.stock != null && item.quantity > item.product.stock;
            return (
              <div key={item.product._id} className={`${styles.item} ${overStock ? styles.itemOver : ''}`}>
                <div className={styles.itemInfo} onClick={() => setEditItem(item)} style={{ cursor: 'pointer' }}>
                  <p className={styles.itemName}>{item.product.name}</p>
                  <p className={`${styles.itemPrice} ${discounted ? styles.itemPriceDisc : ''}`}>
                    {fmt(item.unit_price)}
                    {discounted && <span className={styles.itemOrigPrice}>{fmt(item.product.price)}</span>}
                  </p>
                  {overStock && (
                    <p className={styles.itemStockWarn}><IcoWarning size={12} /> Solo {item.product.stock} disponible{item.product.stock === 0 ? ' (sin stock)' : ''}</p>
                  )}
                </div>
                <div className={styles.qtyControl}>
                  <button className={styles.qtyBtn} onClick={() => updateQty(item.product._id, item.quantity - 1)}>−</button>
                  <span className={`${styles.qty} ${overStock ? styles.qtyOver : ''}`}>{item.quantity}</span>
                  <button className={styles.qtyBtn} onClick={() => updateQty(item.product._id, item.quantity + 1)}>+</button>
                </div>
                <span className={styles.itemTotal}>{fmt(item.total)}</span>
              </div>
            );
          })
        )}
      </div>

      {/* Nota de venta */}
      {items.length > 0 && (
        <div className={styles.noteSection}>
          {noteOpen || note ? (
            <div className={styles.noteInputRow}>
              <input
                className={styles.noteInput}
                placeholder="Nota de la venta…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                autoFocus={noteOpen && !note}
                maxLength={120}
              />
              {note && (
                <button className={styles.noteClear} onClick={() => { setNote(''); setNoteOpen(false); }}><IcoClose size={12} /></button>
              )}
            </div>
          ) : (
            <button className={styles.noteBtn} onClick={() => setNoteOpen(true)}>
              + Nota
            </button>
          )}
        </div>
      )}

      {/* Descuento */}
      {items.length > 0 && (
        <div className={styles.discountSection}>
          {discount > 0 && !discOpen ? (
            <div className={styles.discountApplied}>
              <span className={styles.discountTag}>Descuento −{fmt(discount)}</span>
              <button className={styles.discountClear} onClick={clearDiscount}>×</button>
            </div>
          ) : discOpen ? (
            <div className={styles.discountInput}>
              <div className={styles.discPresets}>
                {[10, 15, 20, 25].map((pct) => (
                  <button
                    key={pct}
                    className={styles.discPresetBtn}
                    onClick={() => { setDiscount(Math.round(subtotal * pct / 100 * 100) / 100); setDiscOpen(false); }}
                  >{pct}%</button>
                ))}
              </div>
              <div className={styles.discountInputRow}>
                <div className={styles.discTypeToggle}>
                  <button
                    className={`${styles.typeBtn} ${discType === 'pct' ? styles.typeBtnActive : ''}`}
                    onClick={() => setDiscType('pct')}
                  >%</button>
                  <button
                    className={`${styles.typeBtn} ${discType === 'fixed' ? styles.typeBtnActive : ''}`}
                    onClick={() => setDiscType('fixed')}
                  >$</button>
                </div>
                <input
                  className={styles.discValueInput}
                  type="number"
                  min="0"
                  max={discType === 'pct' ? 100 : undefined}
                  placeholder={discType === 'pct' ? '10' : subtotal.toFixed(2)}
                  value={discValue}
                  autoFocus
                  onChange={(e) => setDiscValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyDiscount()}
                />
                <button className={styles.discApply} onClick={applyDiscount}><IcoCheck size={14} /></button>
                <button className={styles.discCancel} onClick={() => setDiscOpen(false)}>×</button>
              </div>
            </div>
          ) : (
            <button className={styles.discountBtn} onClick={() => setDiscOpen(true)}>
              + Descuento
            </button>
          )}
        </div>
      )}

      {/* Totales */}
      <div className={styles.totals}>
        {discount > 0 && (
          <>
            <div className={styles.totalRow}>
              <span>Subtotal</span><span>{fmt(subtotal)}</span>
            </div>
            <div className={`${styles.totalRow} ${styles.discountRow}`}>
              <span>Descuento</span><span>−{fmt(discount)}</span>
            </div>
          </>
        )}
        <div className={`${styles.totalRow} ${styles.grandTotal}`}>
          <span>Total</span><strong>{fmt(total)}</strong>
        </div>
      </div>

      {/* CTA */}
      <button
        className="btn btn--primary btn--full"
        style={{ minHeight: 56, fontSize: 16 }}
        disabled={items.length === 0}
        onClick={() => navigate('/checkout')}
      >
        Cobrar {items.length > 0 ? fmt(total) : ''}
      </button>
    </aside>
  );
}
